import { useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  type Auth,
  type User,
} from 'firebase/auth';

import { trackSignup } from '../callables';
import { useAuth } from '../firebase/FirebaseContext';
import { gaSetUserId, gaSignUp, type SignUpMethod } from '../lib/gaPixel';

/**
 * App-level analytics identity hook. Mounted exactly once near the root of
 * the React tree (sibling to `usePageTracking`). Wires GA4 `user_id` to the
 * Firebase UID on every auth state change so pre-signup and post-signup
 * events join under one identifier in GA4. Anonymous Firebase users get a
 * UID immediately; `linkWithCredential` preserves the same UID across the
 * signup boundary, so events from both phases share `user_id` once the
 * wiring is in place.
 *
 * Signup detection (GA4 `sign_up` + the Klaviyo-triggering `trackSignup`
 * callable) does NOT live here (see `reportAccountCreated` below). It used
 * to: this hook watched the (anonymous OR null) → permanent auth-state
 * transition and inferred "this was a signup" from `prev.observed` plus
 * account-metadata timing. That inference broke on a phone Google sign-in
 * (THE-1375): a guest who upgrades via a mobile OAuth redirect has the
 * upgrade finish during Firebase's own startup, before this hook's first
 * callback — so the first state it ever saw was "already a real account",
 * never the guest moment, and no amount of tuning the timing window fixes
 * that (it also mis-fired a SECOND time for any returning user whose
 * account looked "fresh" by the same static metadata check — THE-1375's
 * row 360). Each sign-in door now reports its own verified outcome at the
 * moment it's created instead.
 */

// Two SEPARATE gates, not one. GA4 `sign_up` must stay fired-once-per-uid
// forever — GA4 has no dedupe of its own, so a second event for the same uid
// is a second counted signup in every GA4 report. The `trackSignup` callable
// (the Klaviyo "Signed Up" trigger) is the opposite: a RETRYABLE failure
// (see `RETRYABLE_SIGNUP_FAILURE_REASONS` below) means the server released
// its ledger slot for this uid, and a later call for the SAME uid should be
// allowed to try the callable again. Sharing one gate between the two (as
// this used to) meant clearing it to retry the callable also quietly
// re-armed GA4, double-counting the signup on retry (THE-1375 Shepherd QA
// round 3, finding 1).
const GA_SIGNUP_FIRED_KEY_PREFIX = 'thea:ga_signup_fired:';
const CALLABLE_CLAIMED_KEY_PREFIX = 'thea:signup_callable_claimed:';

/** Resolve the Firebase Auth provider id, defaulting to 'unknown'. */
function detectMethod(user: User): SignUpMethod | string {
  const provider = user.providerData[0]?.providerId;
  return provider ?? 'unknown';
}

/** Per-uid sessionStorage gate; safe under SSR / private-mode storage errors.
 *  Defense-in-depth only — the durable per-uid guard is the server-side
 *  `theaWebSignups/{uid}` ledger (`theaWebTrackSignup.py`), which is what
 *  actually enforces "exactly once" when this client-side gate is bypassed
 *  (a fresh tab, cleared storage, two doors racing). */
function hasFiredGaSignupFor(uid: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(GA_SIGNUP_FIRED_KEY_PREFIX + uid) === '1';
  } catch {
    return false;
  }
}

/** PERMANENT — never cleared by a retry. Set the instant GA4 `sign_up` fires
 *  for this uid, since GA4 has no server-side dedupe to fall back on. */
function markGaSignupFiredFor(uid: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(GA_SIGNUP_FIRED_KEY_PREFIX + uid, '1');
  } catch {
    // private-mode / quota / disabled storage — degrade gracefully. The cost
    // of a missed dedup is one duplicate call per session in a rare edge
    // case; the server-side ledger still only sends the welcome once.
  }
}

/** Has a `trackSignup` attempt already been claimed for this uid in this tab
 *  (either confirmed-sent, or a permanent no-op) — distinct from the GA4
 *  gate above so clearing THIS one (below) never re-arms GA4. */
function hasClaimedCallableFor(uid: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(CALLABLE_CLAIMED_KEY_PREFIX + uid) === '1';
  } catch {
    return false;
  }
}

function markCallableClaimedFor(uid: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(CALLABLE_CLAIMED_KEY_PREFIX + uid, '1');
  } catch {
    // same degrade-gracefully stance as markGaSignupFiredFor.
  }
}

/** Undo `markCallableClaimedFor` — lets a LATER call for this uid retry ONLY
 *  the `trackSignup` callable (GA4's gate above is untouched). Only called
 *  after a RETRYABLE `trackSignup` outcome: the server released its ledger
 *  slot for this uid, so a later attempt can still win it.
 *
 *  "A later call" means another production door reporting the SAME uid in
 *  the SAME tab shortly after — in practice the only real second caller is
 *  the `/claim` redirect return, where `AuthGateContext` and `V4ClaimRoute`
 *  both report within milliseconds of each other. This is best-effort, not
 *  a retry job: a reload or a later visit never calls `reportAccountCreated`
 *  again, so a Klaviyo failure with no second same-tab caller still leaves
 *  that uid unwelcomed. A sweeper that revisits released slots later is a
 *  deliberately separate piece of scope (Kate's call), not this gate's job. */
function clearCallableClaimFor(uid: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(CALLABLE_CLAIMED_KEY_PREFIX + uid);
  } catch {
    // same degrade-gracefully stance as markCallableClaimedFor.
  }
}

/** `trackSignup` outcomes where `theaWebTrackSignup.py` released its ledger
 *  slot rather than confirming a send — the uid got no welcome and is still
 *  eligible for one. ('already_sent' / 'no_email' / 'not_configured' are NOT
 *  here: those are permanent no-ops, not failed attempts.) */
const RETRYABLE_SIGNUP_FAILURE_REASONS: ReadonlySet<string> = new Set([
  'send_failed',
  'klaviyo_error',
]);

/**
 * Call this at the moment a sign-in door VERIFIES an account was just
 * created — never inferred from auth-state timing. Fires the GA4 `sign_up`
 * event plus the same-origin `trackSignup` callable (the Klaviyo "Signed Up"
 * trigger for the welcome/lifecycle flow), each deduped per uid for this tab
 * — but on DIFFERENT terms, see the two gates above: GA4 is once ever, the
 * callable can retry.
 *
 * Called from four production call sites across three doors, each already
 * knowing the account is new the moment it's true, and each firing once per
 * account (a reload or a later visit never calls this again):
 *   - `V4SignInModal.authenticated` (popup / email, every mount of the modal)
 *   - `AuthGateContext`'s redirect-consume effect (mobile OAuth redirect return)
 *   - `V4ClaimRoute`, twice: the popup/email branch and the redirect-return
 *     branch — these two ARE a real same-tab second caller for the SAME uid
 *     (the `/claim` redirect return), which is what the retryable-callable
 *     gate above actually covers in production today.
 */
export function reportAccountCreated(user: User): void {
  const uid = user.uid;
  if (hasClaimedCallableFor(uid)) return;
  // GA4 gate is INDEPENDENT of the callable gate below and never cleared by
  // a retry: a retried callable attempt for a uid GA4 already counted must
  // not count it again.
  if (!hasFiredGaSignupFor(uid)) {
    gaSignUp({ method: detectMethod(user) });
    markGaSignupFiredFor(uid);
  }
  // Marked BEFORE the callable settles — a second door firing for this SAME
  // uid a tick later (two listeners racing) must not double-call the
  // callable. Fire-and-forget: a callable/Klaviyo hiccup must never break
  // signup. The callable reads the email + method from the verified auth
  // token, so no identity is passed here.
  markCallableClaimedFor(uid);
  void trackSignup({}).then(
    (res) => {
      // A RETRYABLE failure means the server released this uid's ledger
      // slot (theaWebTrackSignup.py) — nothing was sent. Marking claimed
      // unconditionally here used to strand that release with no caller
      // ever able to retry it (THE-1375 Shepherd QA round 2, finding 3);
      // clearing the gate lets a later same-tab call for this uid try again.
      if (RETRYABLE_SIGNUP_FAILURE_REASONS.has(res.data?.reason ?? '')) {
        clearCallableClaimFor(uid);
      }
    },
    () => clearCallableClaimFor(uid), // the callable itself never reached the server
  );
}

export function useGaUserIdentity(): void {
  const auth: Auth = useAuth();
  // Dedups a same-uid re-delivery from either listener so each uid change
  // only fires the gtag calls once.
  const prevUidRef = useRef<string | null>(null);

  useEffect(() => {
    // Subscribe to BOTH listeners — same pattern as HeaderAccountMenu.
    // `onAuthStateChanged` is SILENT during the in-place anon→permanent link
    // upgrade where the uid is preserved (linkWithCredential / linkWithPopup).
    // `onIdTokenChanged` fires for that case. `onAuthStateChanged` covers
    // cold-load IndexedDB-restore on page refresh, which `onIdTokenChanged`
    // can miss due to subscription timing during Auth init.
    const handler = (user: User | null) => {
      const currentUid = user?.uid ?? null;
      if (currentUid !== prevUidRef.current) {
        gaSetUserId(currentUid);
      }
      prevUidRef.current = currentUid;
    };
    const unsubAuth = onAuthStateChanged(auth, handler);
    const unsubToken = onIdTokenChanged(auth, handler);
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, [auth]);
}
